import {
  createContext,
  useContext,
  useState,
  ReactElement,
  useMemo,
} from "react";

import { useWindowSize } from "@newsela/angelou";

import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { getTopFiveTags } from "../FilterPanel/filters/Concepts/ConceptTaxonomyModal/utils";
import {
  ConceptNode,
  RawConceptNodeMap,
} from "../FilterPanel/filters/Concepts/Concepts.interface";

interface IConceptTaxonomyProvider {
  children: ReactElement;
}

interface ITaxonomyTagsContextProps {
  allTagsRawData: RawConceptNodeMap;
  draftSelectedIds: Set<string>;
  isTabletOrMobileView: boolean;
  handleConceptSubmit: () => void;
  selectedIds: Set<string>;
  setSelectedIds: (id: Set<string>) => void;
  setDraftSelectedIds: (id: Set<string>) => void;
  topFiveElaTags: ConceptNode[];
  updateSearchParams: (ids: Set<string>) => void;
}

const initialContextValue = {
  allTagsRawData: {},
  draftSelectedIds: new Set([]),
  isTabletOrMobileView: false,
  handleConceptSubmit: () => null,
  selectedIds: new Set([]),
  setSelectedIds: () => null,
  setDraftSelectedIds: () => null,
  topFiveElaTags: [],
  updateSearchParams: () => null,
};

const TaxonomyTagsContext =
  createContext<ITaxonomyTagsContextProps>(initialContextValue);

export const ConceptTaxonomyProvider = ({
  children,
}: IConceptTaxonomyProvider) => {
  const { width: windowWidth } = useWindowSize();
  const {
    getters: { taxonomyConcepts },
    handlers: {
      appendSearchParam,
      deleteSearchParamValues,
      getSearchParamValues,
    },
  } = useSearchPageContext();

  const MD_WIDTH = 1024;
  const isTabletOrMobileView = windowWidth >= 0 && windowWidth <= MD_WIDTH;

  const topicsParamValues = getSearchParamValues(
    LangstonParameterSchema.topics.key,
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(topicsParamValues),
  );
  const [draftSelectedIds, setDraftSelectedIds] = useState<Set<string>>(
    new Set(),
  );

  const topFiveElaTags = useMemo(() => {
    return getTopFiveTags(taxonomyConcepts);
  }, [JSON.stringify(taxonomyConcepts)]);

  // Used as a reference map for selected filter chips in FilterControls.
  const allTagsRawData = useMemo(() => {
    return Object.assign(
      {},
      taxonomyConcepts?.ela?.items || {},
      taxonomyConcepts?.science?.items || {},
      taxonomyConcepts?.socialStudies?.items || {},
    );
  }, [JSON.stringify(taxonomyConcepts)]);

  const updateSearchParams = (ids: Set<string>) => {
    if (ids.size) {
      deleteSearchParamValues(LangstonParameterSchema.topics.key);
      ids.forEach((id) => {
        appendSearchParam(LangstonParameterSchema.topics.key, id);
      });
    } else {
      deleteSearchParamValues(LangstonParameterSchema.topics.key);
    }
  };

  const handleConceptSubmit = () => {
    setSelectedIds(draftSelectedIds);
    setDraftSelectedIds(new Set());
    updateSearchParams(draftSelectedIds);
  };

  return (
    <TaxonomyTagsContext.Provider
      value={{
        allTagsRawData,
        draftSelectedIds,
        isTabletOrMobileView,
        handleConceptSubmit,
        selectedIds,
        setSelectedIds,
        setDraftSelectedIds,
        topFiveElaTags,
        updateSearchParams,
      }}
    >
      {children}
    </TaxonomyTagsContext.Provider>
  );
};

export const useConceptTaxonomyProvider = () => useContext(TaxonomyTagsContext);

ConceptTaxonomyProvider.displayName = "ConceptTaxonomyProvider";
